import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import ThesisGateBanner from "../components/coach/ThesisGateBanner";
import ThesisReview from "../components/coach/ThesisReview";
import DayZeroChat from "../components/coach/DayZeroChat";
import MemoryCard from "../components/coach/MemoryCard";
import WeeklyNarrative from "../components/coach/WeeklyNarrative";
import AdminPreviewTierToggle from "../components/paywall/AdminPreviewTierToggle";
import {
  Radar as RadarIcon, ShieldAlert, Sparkles, Lock, Quote,
  Loader2, ClipboardCheck,
} from "lucide-react";

/**
 * Coach — the Radar feed.
 *
 * iter133 Phase 6 (structural). Live nudge emission is deliberately
 * NOT wired yet — it depends on Phase 5 (Size Anomaly Rule), which
 * is BLOCKED until the Delta private WS is verified live + reliable
 * with confirmed read-only key scopes. See ai_coach_D0_report.md.
 *
 * What this page DOES today:
 *   1. Contract Reflection — echoes the user's own
 *      stop-trading-condition sentence from today's Session
 *      Contract back at them. The whole reason we ask them to
 *      write it in the first place.
 *   2. Empty Contract-Breach section (free tier) — will populate
 *      when a live order breaches one of the Session Contract caps.
 *   3. Locked Historical-Signals section (Elite tier) — will
 *      populate when notional > 3× the personal 20-trade median
 *      for a symbol. Free users see the locked state + upgrade CTA.
 *
 * Tier gate: page itself is free-viewable (helps free users
 * understand the differentiator so they convert). Historical
 * section is Elite-gated inline.
 */
export default function Coach() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/coach/radar");
        setData(data);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isElite = !!(data?.elite);
  const reflection = data?.contract_reflection;
  const hasContract = !!data?.session_contract;

  return (
    <div className="space-y-8" data-testid="coach-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <span className="w-12 h-12 rounded-sm border border-[var(--bt-border)] flex items-center justify-center">
          <RadarIcon className="w-5 h-5 text-[var(--bt-accent)]" />
        </span>
        <div className="flex-1">
          <p className="bt-label">AI Coach</p>
          <h1 className="font-heading text-4xl">Radar</h1>
          <p className="text-sm text-[var(--bt-text-muted)] mt-1 max-w-2xl">
            Live anomalies against the limits you set for yourself today.
            Deterministic — every number is computed on your own data,
            never made up.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[var(--bt-text-muted)] py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading Radar…
        </div>
      ) : (
        <>
          {/* ── Day Zero onboarding — first-run gentle expand ─── */}
          <DayZeroChat variant="full" />

          {/* ── Thesis gate — live entries awaiting a one-liner ── */}
          <ThesisGateBanner variant="full" />

          {/* ── Contract Reflection ────────────────────────────── */}
          {hasContract && reflection ? (
            <section
              className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-6 space-y-3"
              data-testid="coach-contract-reflection"
            >
              <div className="flex items-center gap-3">
                <Quote className="w-4 h-4 text-[var(--bt-accent)]" />
                <p className="bt-label">Your Stop-Trading Rule for {reflection.date_utc}</p>
              </div>
              <blockquote
                className="border-l-2 border-[var(--bt-accent)] pl-4 text-lg font-heading text-[var(--bt-text)]"
                data-testid="coach-contract-reflection-quote"
              >
                “{reflection.stop_trading_condition}”
              </blockquote>
              <p className="text-xs text-[var(--bt-text-muted)]">
                You wrote this yourself this morning — Coach will surface it
                again if a live trade crosses one of your own limits.
              </p>
            </section>
          ) : (
            <section
              className="bg-[var(--bt-surface)] border border-dashed border-[var(--bt-border)] rounded-sm p-6 flex items-center justify-between gap-4 flex-wrap"
              data-testid="coach-no-contract-cta"
            >
              <div className="flex items-start gap-3">
                <ClipboardCheck className="w-5 h-5 text-[var(--bt-text-muted)] mt-0.5" />
                <div>
                  <p className="font-heading text-lg">No session contract set for today</p>
                  <p className="text-sm text-[var(--bt-text-muted)] max-w-xl mt-1">
                    Coach can't nudge you against limits you haven't chosen.
                    Two minutes on Settings and you're set for the day.
                  </p>
                </div>
              </div>
              <Link to="/settings">
                <Button
                  data-testid="coach-set-contract-btn"
                  className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm h-11"
                >
                  Set today's contract
                </Button>
              </Link>
            </section>
          )}

          {/* ── Contract Breaches — Free tier ──────────────────── */}
          <section
            className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-6 space-y-4"
            data-testid="coach-contract-breach-section"
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <ShieldAlert className="w-4 h-4 text-[var(--bt-accent)]" />
                <div>
                  <p className="bt-label">Contract Breaches · Free</p>
                  <h2 className="font-heading text-xl">Against Your Own Limits</h2>
                </div>
              </div>
              <span className="text-xs text-[var(--bt-text-muted)] font-mono uppercase">
                Free tier
              </span>
            </div>

            {data?.contract_breaches?.length > 0 ? (
              <ul className="space-y-2" data-testid="coach-contract-breach-list">
                {data.contract_breaches.map((b) => (
                  <li key={b.id} className="text-sm p-3 border border-[var(--bt-loss)] rounded-sm">
                    {b.headline}
                  </li>
                ))}
              </ul>
            ) : (
              <div
                className="text-sm text-[var(--bt-text-muted)] py-6 text-center border border-dashed border-[var(--bt-border)] rounded-sm"
                data-testid="coach-contract-breach-empty"
              >
                No breaches yet — Coach is watching.
                {data?.watching_symbols?.length
                  ? ` ${data.watching_symbols.length} symbol${data.watching_symbols.length === 1 ? "" : "s"} on the private feed.`
                  : " Live feed will show up here once your broker connection is streaming."}
              </div>
            )}
          </section>

          {/* ── Historical Signals — Elite gate ─────────────────── */}
          <section
            className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-6 space-y-4"
            data-testid="coach-historical-section"
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3">
                <Sparkles className="w-4 h-4 text-[var(--bt-accent)]" />
                <div>
                  <p className="bt-label">Historical Signals · Elite</p>
                  <h2 className="font-heading text-xl">Against Your Own History</h2>
                </div>
              </div>
              <span className="text-xs text-[var(--bt-accent)] font-mono uppercase">
                Elite
              </span>
            </div>

            {!isElite ? (
              <div
                className="border border-dashed border-[var(--bt-accent)] rounded-sm p-6 flex items-center gap-4 flex-wrap"
                data-testid="coach-historical-locked"
              >
                <Lock className="w-5 h-5 text-[var(--bt-accent)]" />
                <div className="flex-1 min-w-[240px]">
                  <p className="font-heading text-lg">Personal outliers, narrated</p>
                  <p className="text-sm text-[var(--bt-text-muted)] mt-1">
                    Elite unlocks Coach's second layer — a nudge when a trade is
                    3× your usual size, plus an AI-narrated one-liner explaining
                    what pattern it matches from your history. Contract breaches
                    stay free.
                  </p>
                </div>
                <Link to="/pricing">
                  <Button
                    data-testid="coach-upgrade-btn"
                    className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm h-11"
                  >
                    Upgrade to Elite
                  </Button>
                </Link>
              </div>
            ) : data?.historical_alerts?.length > 0 ? (
              <ul className="space-y-2" data-testid="coach-historical-list">
                {data.historical_alerts.map((a) => (
                  <li key={a.id} className="text-sm p-3 border border-[var(--bt-accent)] rounded-sm">
                    {a.headline}
                  </li>
                ))}
              </ul>
            ) : (
              <div
                className="text-sm text-[var(--bt-text-muted)] py-6 text-center border border-dashed border-[var(--bt-border)] rounded-sm"
                data-testid="coach-historical-empty"
              >
                {data?.profile_completed ? (
                  <>
                    No outliers detected. Coach is comparing every trade against
                    your{" "}
                    <span className="text-[var(--bt-accent)]">Day Zero setup</span>
                    {" "}until you've closed 20 trades per symbol — then it
                    switches to your real history.
                  </>
                ) : (
                  <>
                    No historical outliers detected. Coach needs at least 20 closed
                    trades per symbol to build a personal baseline, or a completed{" "}
                    <span className="text-[var(--bt-accent)]">Day Zero setup</span>{" "}
                    for a starter baseline.
                  </>
                )}
              </div>
            )}
          </section>

          {/* ── Thesis review — reviewed theses paired with outcome ── */}
          <ThesisReview />

          {/* ── D6b Weekly Narrative — deterministic ISO-week recap ── */}
          <WeeklyNarrative />

          {/* ── D6a Memory — typed, user-editable, DPDP-compliant ── */}
          <MemoryCard />
        </>
      )}

      {/* ── D7a Admin preview-tier toggle (admin only) ── */}
      <AdminPreviewTierToggle />
    </div>
  );
}
